/**
 * Route guard factory — deny navigation when the current admin
 * lacks a required permission token.
 *
 * Usage in `app.routes.ts`:
 *
 *   {
 *     path: "products",
 *     canActivate: [permissionGuard("products:list")],
 *     loadComponent: () => import("./pages/products-list/products-list")
 *   }
 *
 * If the PermissionsService hasn't loaded yet (first navigation
 * after login), the guard kicks off the fetch and defers routing
 * until the token set arrives. Denied users are redirected to
 * `/access-denied` (falls back to root if the route doesn't exist).
 */
import {inject} from "@angular/core";
import {CanActivateFn, Router, UrlTree} from "@angular/router";
import {Observable, map} from "rxjs";
import {PermissionsService} from "../services/permissions.service";

/**
 * A guard `permissionGuard` built, carrying the tokens it checks — so code
 * that must name the page's own permission (the Google Sheets export sends
 * the table's resource: `TableExportService`) reads it from the route instead
 * of repeating it.
 */
export type PermissionGuardFn = CanActivateFn & {readonly permissionTokens: readonly string[]};

export function permissionGuard(token: string | string[]): CanActivateFn {
    const tokens = Array.isArray(token) ? token : [token];
    const guard = (): boolean | UrlTree | Observable<boolean | UrlTree> => {
        const perms = inject(PermissionsService);
        const router = inject(Router);
        const check = (): true | UrlTree => {
            const allowed = perms.canAny(tokens);
            return allowed ? true : router.parseUrl("/access-denied");
        };
        if (!perms.loaded()) {
            return perms.load().pipe(map(() => check()));
        }
        return check();
    };
    return Object.assign(guard, {permissionTokens: tokens}) as PermissionGuardFn;
}
